/**
 * Sanitize a `next` redirect target that came from a query string.
 *
 * Untrusted values must never reach `router.push`/`router.replace`: Next.js
 * executes `javascript:` URLs handed to the router in the context of the page,
 * so an unsanitized `?next=` is XSS. Absolute and protocol-relative URLs are
 * open redirects that can hand a freshly minted session to another origin.
 *
 * Only same-origin, single-slash absolute paths survive. Backslashes are
 * rejected outright because browsers normalize them to `/`, which makes
 * `/\evil.example` protocol-relative in practice.
 */
export function safeNext(raw: string | null | undefined, fallback: string): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/')) return fallback;
  if (raw.startsWith('//')) return fallback;
  if (raw.includes(String.fromCharCode(92))) return fallback;
  if (hasControlChar(raw)) return fallback;
  return raw;
}

function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}
